'use client';

import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react';
import { useAnalytics } from '@repo/segment/Analytics';
import { useAnonymousId } from '@repo/segment/components/AnonymousIdProvider';
import { useClientOnlyConstant } from '@repo/segment/hooks/useClientOnlyConstant';
import { getDeviceIdWithoutHmac } from '@repo/segment/utils/getDeviceId';
import type { ExperimentConfig } from '../experiments/types';
import type { ABSmartlyContextType, ABSmartlyProviderProps } from '../types';
import { hydrateClientContext } from '../utils/context';
import { useDeviceType } from '../utils/deviceType/hooks';
import { getABSmartlyEventLogger } from '../utils/eventLogging';
import type { SupportedExperimentVariants } from '../utils/types';
import { abSmartlySdkConfig } from './config';
import { LogStaticExperiments } from './LogStaticExperiments';

export const ABSmartlyContext = createContext<ABSmartlyContextType | null>(null);
export const useABSmartlyContext = () => useContext(ABSmartlyContext);

export const ABSmartlyProvider = ({
  contextData,
  children,
  staticExperimentResult,
  staticExperimentName,
  experiments,
}: PropsWithChildren<ABSmartlyProviderProps> & {
  staticExperimentResult: SupportedExperimentVariants;
  staticExperimentName?: string;
  experiments: ExperimentConfig[];
}) => {
  const anonymousId = useAnonymousId();
  const { analytics, deployableVersion, gitSha } = useAnalytics();
  const [smartlyContext, setSmartlyContext] = useState<ABSmartlyContextType | null>(null);
  const deviceType = useDeviceType();
  const deviceId = useClientOnlyConstant(() => getDeviceIdWithoutHmac(), '');

  useEffect(() => {
    if (smartlyContext || !contextData || !analytics) {
      return;
    }

    const clientSideContext = hydrateClientContext({
      sdkConfig: {
        ...abSmartlySdkConfig,
        eventLogger: getABSmartlyEventLogger({
          analytics,
          anonymousId,
          deployableVersion,
          deviceId,
          gitSha,
          staticExperimentResult,
          experiments,
        }),
      },
      anonymousId,
      deviceType,
      contextData,
    });
    setSmartlyContext(clientSideContext);
  }, [analytics, anonymousId, deviceId, deviceType, contextData]);

  return (
    <ABSmartlyContext.Provider value={smartlyContext}>
      <LogStaticExperiments
        serverSideExperimentDecision={staticExperimentResult}
        staticExperimentName={staticExperimentName}
      >
        {children}
      </LogStaticExperiments>
    </ABSmartlyContext.Provider>
  );
};
